import React from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "@/components/useScrollAnimation";
import type { LucideIcon } from "lucide-react";

export const Section = ({ children, bg, className }: { children: React.ReactNode; bg?: boolean; className?: string }) => (
  <section className={`py-16 sm:py-24 ${bg ? "bg-muted/50" : ""} ${className ?? ""}`}>
    <div className="container mx-auto px-6 lg:px-10 max-w-5xl">{children}</div>
  </section>
);

export const SectionDivider = () => (
  <div className="relative h-px mx-auto max-w-5xl">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border to-transparent" />
  </div>
);

export const SectionHeader = ({ label, title, icon: Icon }: { label: string; title?: string; icon?: LucideIcon }) => {
  const { ref, isInView } = useScrollAnimation();
  return (
    <motion.div
      ref={ref}
      className="mb-10"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="w-6 h-0.5 rounded-full bg-primary" />
        <span className="text-sm font-semibold text-primary uppercase tracking-widest">{label}</span>
      </div>
      {title && (
        title.length > 80
          ? <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed mt-2">{title}</p>
          : <h2 className="text-xl sm:text-2xl font-bold text-foreground">{title}</h2>
      )}
    </motion.div>
  );
};

export const AnimatedCard = ({ children, index }: { children: React.ReactNode; index: number }) => {
  const { ref, isInView } = useScrollAnimation();
  return (
    <motion.div
      ref={ref}
      className="bg-card border border-border rounded-xl p-7 transition-all duration-300 hover:border-primary/20"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -4, boxShadow: "0 20px 40px -12px hsla(212, 96%, 58%, 0.12)" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      {children}
    </motion.div>
  );
};

export const FullBleedImage = ({ image }: { image: { src: string; alt: string; overlayText?: string; overlaySubtext?: string } }) => (
  <section className="relative">
    <div className="aspect-[21/9] sm:aspect-[3/1] overflow-hidden">
      <motion.img
        src={image.src}
        alt={image.alt}
        className="w-full h-full object-cover"
        loading="lazy"
        initial={{ opacity: 0, scale: 1.05 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      />
      {image.overlayText && (
        <div className="absolute inset-0 bg-primary/80 flex items-center justify-center">
          <div className="text-center px-6 max-w-3xl">
            <motion.h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {image.overlayText}
            </motion.h2>
            {image.overlaySubtext && (
              <motion.p
                className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                {image.overlaySubtext}
              </motion.p>
            )}
          </div>
        </div>
      )}
    </div>
  </section>
);
